import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import DemoLayout from "@/components/DemoLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoLanding() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/demo/data`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ personas: [], jobs: [], disclaimer: "" }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <DemoLayout>
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-8">
        <div className="terminal-label text-primary mb-6">[ DEMO · CHOOSE A PERSONA ]</div>
        <h1 className="serif-mod text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] mb-6" style={{ fontWeight: 500 }}>
          Browse the actual product as one of two example <span className="whimsy text-primary text-[1.08em]">creatives.</span>
        </h1>
        <p className="text-sm sm:text-base text-foreground/75 leading-relaxed max-w-2xl mb-2">
          Pick a persona. You will land in their feed, browse their profile, click into a real Application Review with tailored materials, then run an ATS A/B comparison to see the score lift.
        </p>
        <p className="text-[11px] tracking-[0.18em] uppercase text-foreground/50 font-mono">
          No signup. No data saved. Tailoring is pre-baked, identical for every visitor.
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pb-24">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground text-xs uppercase tracking-[0.3em]">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading personas
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {(data?.personas || []).map((p, idx) => (
              <PersonaCard key={p.id} persona={p} index={idx} />
            ))}
          </div>
        )}
      </section>
    </DemoLayout>
  );
}

function PersonaCard({ persona, index }) {
  const accentColors = ["hsl(var(--primary))", "hsl(var(--accent-green))"];
  const accent = accentColors[index % accentColors.length];
  const heroMatch = (persona.match_table || []).reduce((m, x) => Math.max(m, x.match_score || 0), 0);

  return (
    <Link
      to={`/demo/dashboard?persona=${persona.id}`}
      data-testid={`demo-persona-card-${persona.id}`}
      className="brut-tile p-6 md:p-8 hover:border-primary transition-colors group block"
      style={{ borderTopWidth: 3, borderTopColor: accent }}
    >
      <div className="flex items-start gap-5 mb-5">
        {persona.headshot_url && (
          <img src={persona.headshot_url} alt={persona.name} className="w-20 h-20 object-cover border border-foreground/15 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="terminal-label mb-1" style={{ color: accent }}>[ PERSONA {String(index + 1).padStart(2, "0")} ]</div>
          <h3 className="serif-mod text-2xl md:text-3xl tracking-tight" style={{ fontWeight: 500 }}>{persona.name}</h3>
          <p className="text-sm text-foreground/70 mt-1">{persona.title}</p>
          <p className="text-[11px] tracking-[0.18em] uppercase text-foreground/50 font-mono mt-1">
            {persona.location} · {persona.years_experience}yr
          </p>
        </div>
      </div>

      <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed mb-5">{persona.bio}</p>

      <div className="flex items-center justify-between pt-4 border-t border-foreground/15">
        <div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-foreground/45 font-mono">Top match</div>
          <div className="font-display text-2xl tabular-nums" style={{ color: accent }}>{heroMatch}</div>
        </div>
        <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-foreground/60 group-hover:text-primary inline-flex items-center gap-1.5">
          Try as {persona.name.split(" ")[0]} <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}
